"use client";

import React, { useState, useEffect } from "react";
import { WifiOff, RefreshCw, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * OfflineBanner — Displays a persistent, non-intrusive alert when
 * the device loses network connectivity, confirming offline mode availability.
 */
export default function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setDismissed(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
      setDismissed(false);
    };

    if (typeof window !== "undefined") {
      setIsOffline(!navigator.onLine);
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  if (!isOffline || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="bg-y text-black font-syne text-xs font-bold px-4 py-2 flex items-center justify-between shadow-md relative z-[200] border-b border-black/10"
        role="alert"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 max-w-4xl mx-auto flex-1 justify-center text-center">
          <WifiOff size={15} className="shrink-0 animate-pulse" />
          <span>
            Offline Mode Active — Cached course notes, formulas, and study tools
            remain accessible.
          </span>
          <button
            onClick={() => window.location.reload()}
            className="ml-2 inline-flex items-center gap-1 underline text-[11px] font-black uppercasehover:opacity-80 transition-opacity"
          >
            <RefreshCw size={11} /> Reconnect
          </button>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 roundedhover:bg-muted/20 transition-colors ml-2"
          aria-label="Dismiss offline banner"
        >
          <X size={14} />
        </button>
      </motion.aside>
    </AnimatePresence>
  );
}
